import React, { FC, useCallback, useEffect } from "react";

import { KpkFolder, Language } from "@keepeek/commons";
import {
  KBreadcrumb,
  KButton,
  KFolderBrowse,
  KMosaic,
  KMosaicAnimationType,
  KpkMosaicLabelCustomElement,
  KMosaicElementDetail,
  KMosaicElementSize,
  KMosaicProps,
  SanitizeHtml,
  useThemeColors,
  useMosaicWidget,
} from "@keepeek/refront-components";
import { Container, LinearProgress, Typography, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import * as Sentry from "@sentry/nextjs";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";

import { KIcon } from "../../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { FolderBrowse } from "../../../models/configuration/components/folderBrowse";
import { MosaicElement } from "../../../models/configuration/components/mosaic";
import { TranslationSchema } from "../../../models/configuration/labels";
import { useActiveFiltersFromURL } from "../../../providers/businessFilter/hooks/useActiveFiltersFromURL";
import { useConfiguration } from "../../../providers/config/hooks";
import { useInitConfig } from "../../../providers/config/hooks/init";
import { useImageOptim } from "../../../providers/config/hooks/useImageOptim";
import { useCurrentLocale } from "../../../providers/i18n/hooks";
import { PagesPath } from "../../App/utils";
import ImageOptim from "../../ImageOptim";
import { WidgetUIState } from "../atoms";
import { useWidgetState } from "../hooks";
import KCarouselWidget, { KCarouselWidgetProps } from "../KCarouselWidget";
import { KWidgetProps } from "../models";
import { ComponentNextLink, parseOptionsInternalUrl } from "../utils/parseInternalUrl";

export type KMosaicWidgetProps = {
  widgetId: string;
  detailFullScreen: boolean;
  size: KMosaicElementSize;
  animationType: KMosaicAnimationType;
  folderIds: KpkFolder["id"][];
  mosaicElements?: MosaicElement[];
  title: string;
  carousel: boolean;
  showSubTitle: boolean;
  centeredMosaics: boolean;
  inheritedContainer: boolean;
  backgroundColor: string;
  borderRadius: string;
  labelCustom?: KpkMosaicLabelCustomElement;
  content: TranslationSchema[];
} & KWidgetProps;

const KMosaicWidget: FC<React.PropsWithChildren<KMosaicWidgetProps>> = function ({
  widgetId,
  title,
  content,
  folderIds,
  mosaicElements,
  carousel,
  centeredMosaics,
  inheritedContainer,
  backgroundColor,
  showSubTitle,
  size,
  animationType,
  detailFullScreen,
  type,
  borderRadius,
  labelCustom,
}) {
  const locale = useCurrentLocale();
  const { pathname } = useRouter();
  const { t } = useTranslation("refront-components");
  const { convertColor } = useThemeColors();
  const {
    showFolderCover = false,
    showSubFolderLink = false,
    showMainActionLink = true,
  } = useConfiguration<FolderBrowse>(CONFIGURATION_SECTION_PATH.COMPONENTS_FOLDER_BROWSE) ?? {
    showFolderCover: false,
    showSubFolderLink: false,
    showMainActionLink: true,
  };

  const { setWidgetState, widgetState } = useWidgetState({
    id: widgetId,
    type,
  });

  const { initConfig } = useInitConfig();
  const { setActiveFilters } = useActiveFiltersFromURL();
  const sentryCallback = ({ category, message }) => Sentry.addBreadcrumb({ category, message });

  const {
    folderTrees,
    currentSelectedId,
    handleElementClick,
    loadingFolderTree,
    handleHideButton,
    elements,
    folders,
    folderTree,
    foldersVisible,
    openFolder,
    elementSelected,
    search,
    handleFolderClick,
    loading: mosaicLoading,
  } = useMosaicWidget({
    folderIds,
    mosaicElements,
    widgetId,
    countMedias: showSubTitle,
    clientIp: initConfig?.clientIp,
    showSubFolderLink,
    setActiveFilters,
    sentryCallback,
    currentLocale: locale as Language,
  });

  useEffect(() => {
    if (folderTrees && folderTrees.length === 0 && mosaicElements && mosaicElements.length === 0) {
      // Don't show if we have no element in the mosaic
      setWidgetState({ state: WidgetUIState.NULL });
    } else {
      setWidgetState({
        state: WidgetUIState.RENDERED,
        elementCount:
          !!mosaicElements && mosaicElements.length > 0
            ? mosaicElements?.length
            : folderTrees?.length ?? 0,
      });
    }
  }, [folderTrees, setWidgetState, mosaicElements]);

  const { loading, shouldOptimize } = useImageOptim();
  const theme = useTheme();

  const _handleButtonSearch = useCallback(
    (id: KpkFolder["id"]) => {
      const correspondingFolder = folders?.find((f) => f.id === id);
      if (!correspondingFolder) {
        // Fallback
        search({ folderId: id, extraParams: { fc: id } });
      } else {
        // Nominal case
        search({
          folderId: correspondingFolder.id,
          extraParams: { fc: correspondingFolder.id },
          folderTitle: correspondingFolder.title,
          treeMediaCount: correspondingFolder.treeMediaCount,
        });
      }
      if (pathname === PagesPath.HOME_PAGE) {
        // TODO: should not exist like this, we should have the open state kept into the URL
        // on home page, hide the folders when we navigate on the search page
        // so when we come back to home, we start from fresh
        handleHideButton();
      }
    },
    [folders, handleHideButton, pathname, search],
  );

  if (widgetState.state !== WidgetUIState.RENDERED) {
    return null;
  }

  let labelCustomConvertedColor: KpkMosaicLabelCustomElement | undefined = labelCustom;
  if (labelCustom) {
    labelCustomConvertedColor = {
      ...labelCustom,
      backgroundTextColor: convertColor(labelCustom?.backgroundTextColor) || "",
      textColor: convertColor(labelCustom?.textColor) || "",
    };
  }

  const configuration: KMosaicProps | KCarouselWidgetProps = {
    currentSelectedElement: elementSelected || null,
    mosaicLoading,
    clickDuringLoadingElement: undefined,
    elementSize: size,
    elementDetailFullScreenWidth: detailFullScreen,
    elements,
    onElementClick: (el) => handleElementClick(el),
    centeredMosaics,
    inheritedContainer,
    borderRadius: borderRadius ?? `${theme.shape.borderRadius}px`,
    labelCustom: labelCustomConvertedColor,
    animationType: animationType,
    imageComponent: {
      active: shouldOptimize,
      component: (src) =>
        src && (
          <Box sx={{ overflow: "hidden", position: "relative", height: "100%" }}>
            <ImageOptim
              src={src}
              alt={""}
              fill
              style={{ objectFit: "cover", objectPosition: "center center", borderRadius }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={src}
                alt={""}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center center",
                  borderRadius,
                }}
              />
            </ImageOptim>
          </Box>
        ),
    },
    elementDetail: (
      <>
        {loadingFolderTree && (
          <LinearProgress
            sx={{
              marginBottom: (theme) => theme.spacing(-0.5),
            }}
          />
        )}
        <KMosaicElementDetail
          headerElement={
            <KBreadcrumb
              items={(folders || []).map((folder) => ({
                id: folder.id.toString(),
                label: folder?.title ?? "",
                onClick: (id) => {
                  openFolder(parseInt(id));
                },
              }))}
              buttonLabel={
                t("components.home.widget.mosaic.breadcrumb.button-see-all.label") as string
              }
              onButtonClick={(id) => _handleButtonSearch(Number(id))}
              loading={!folders}
              KIcon={KIcon}
            />
          }
          contentElement={
            !loading &&
            foldersVisible && (
              <KFolderBrowse
                folders={folderTree?.children ?? []}
                onFolderClick={(folder) =>
                  handleFolderClick({
                    folderId: folder.id,
                    extraParams: { fc: currentSelectedId },
                    folderTitle: folder.title,
                    treeMediaCount: folder.treeMediaCount,
                    callback: (context) => {
                      if (context === "search" && pathname === PagesPath.HOME_PAGE) {
                        // TODO: should not exist like this, we should have the open state kept into the URL
                        // on home page, hide the folders when we navigate on the search page
                        // so when we come back to home, we start from fresh
                        handleHideButton();
                      }
                    },
                  })
                }
                onSubFolderClick={(folder) => openFolder(folder.id)}
                loading={!folderTree}
                showFolderCover={showFolderCover}
                showSubFolderLink={showSubFolderLink}
                showMainActionLink={showMainActionLink}
                imageComponent={{
                  active: shouldOptimize && showFolderCover,
                  component: (src, className, alt) =>
                    src && (
                      <ImageOptim
                        src={src}
                        className={className}
                        alt={alt || ""}
                        width={Number(theme.spacing(8).replace("px", ""))}
                        height={Number(theme.spacing(8).replace("px", ""))}
                        style={{ borderRadius }}
                      >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={src}
                          className={className}
                          width={theme.spacing(8).replace("px", "")}
                          height={theme.spacing(8).replace("px", "")}
                          alt={alt || ""}
                          style={{ borderRadius }}
                        />
                      </ImageOptim>
                    ),
                }}
                KIcon={KIcon}
              />
            )
          }
          headerActionElement={
            <KButton variant="outlined" buttonName="hide-folders" onClick={handleHideButton}>
              {foldersVisible ? (
                <KIcon sx={{ marginRight: "8px" }} className="fa-solid fa-eye-slash" />
              ) : (
                <KIcon sx={{ marginRight: "8px" }} className="fa-solid fa-eye" />
              )}
              {foldersVisible
                ? t("components.home.widget.mosaic.detail.button-hide.label")
                : t("components.home.widget.mosaic.detail.button-show.label")}
            </KButton>
          }
        />
      </>
    ),
  };

  let contentValue;
  if (!!content && content.length > 0) {
    contentValue = content.find((c) => c.language === locale)?.value;
  }

  return (
    <Box
      style={{ backgroundColor: convertColor(backgroundColor), width: "100%" }}
      className={clsx("KMosaicWidget")}
    >
      {/* TODO: remove title when the legacy mode will have rolled out */}
      {!!title && (
        <Container
          sx={{
            marginBottom: (theme) => theme.spacing(3),
          }}
        >
          <Typography variant="h5" component="h2">
            {title}
          </Typography>
        </Container>
      )}
      {!!contentValue && (
        <Container
          sx={{
            marginBottom: (theme) => theme.spacing(3),
          }}
        >
          <SanitizeHtml
            sanitizeOptions={{ allowedAttributes: false, allowedTags: false }}
            parseOptions={parseOptionsInternalUrl}
            html={contentValue}
          />
        </Container>
      )}
      {carousel ? (
        <KCarouselWidget {...configuration} type={type} ComponentNextLink={ComponentNextLink} />
      ) : (
        <KMosaic {...configuration} ComponentNextLink={ComponentNextLink} KIcon={KIcon} />
      )}
    </Box>
  );
};

export default KMosaicWidget;
